import { AudioRouteType } from '../voice/arenaAudioRouter';
import { CARD_FAN_AUDIO_DATA_URI } from '../assets/cardFanAudio';
import { CardAudioSynthesizer } from '../engine/audio/CardAudioSynthesizer';
import { GameEventAudioSynthesizer } from '../engine/audio/GameEventAudioSynthesizer';
import { OutcomeAudioSynthesizer } from '../engine/audio/OutcomeAudioSynthesizer';
import { IArenaAudioPlatformAdapter } from '../engine/audio/platform/IArenaAudioPlatformAdapter';
import { ArenaAudioPlatformFactory } from '../engine/audio/platform/ArenaAudioPlatformFactory';
import { arenaLogger } from '../utils/arenaLogger';

class ArenaAudioEngine {
  private ctx: AudioContext | null = null;
  private isMuted: boolean = false;
  private isVoiceActive: boolean = false;
  private masterGainNode: GainNode | null = null;
  private compressorNode: DynamicsCompressorNode | null = null;
  private lastTrickWonTime: number = 0;
  private lastCardToFeltTime: number = 0;
  private lastCardSelectTime: number = 0;
  private lastBidChimeTime: number = 0;
  private lastPlayerJoinTime: number = 0;
  private lastPlayerLeaveTime: number = 0;
  private lastSpectatorJoinTime: number = 0;
  private lastSpectatorLeaveTime: number = 0;
  private currentRoute: AudioRouteType = 'speaker';
  private cardFanBuffer: AudioBuffer | null = null;
  private isDecodingCardFan: boolean = false;
  private unlockListenersAttached: boolean = false;
  private unlockAudioHandler: (() => void) | null = null;
  private visibilityListener: (() => void) | null = null;
  private focusListener: (() => void) | null = null;
  private pageshowListener: (() => void) | null = null;
  private lastBackgroundTime: number = 0;
  private needsHardRecovery: boolean = false;
  private platformAdapter: IArenaAudioPlatformAdapter = ArenaAudioPlatformFactory.createAdapter();
  private listeners: Set<() => void> = new Set();

  constructor() {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('atl_arena_sound_muted');
        if (saved === 'true') {
          this.isMuted = true;
        }
      } catch {}
      this.attachUnlockListeners();

      // Manage background suspension and proactive recovery when multitasking / tab switching
      if (typeof document !== 'undefined') {
        const onVisibilityChange = () => {
          if (document.hidden) {
            // App went to background / screen locked: record timestamp and notify adapter
            this.lastBackgroundTime = Date.now();
            if (this.platformAdapter.platformName === 'ios') {
              this.needsHardRecovery = true;
            }
            this.platformAdapter.onBackground(this.ctx);
            this.notifyListeners();
          } else {
            // App returned to foreground: determine background duration
            const elapsed = this.lastBackgroundTime > 0 ? Date.now() - this.lastBackgroundTime : 0;
            if (this.platformAdapter.platformName === 'ios' || elapsed > 2000) {
              // Deep sleep, phone screen lock, or iOS backgrounding: mark hard recovery required
              this.needsHardRecovery = true;
            }
            this.recoverAudio();
          }
        };
        this.visibilityListener = onVisibilityChange;
        document.addEventListener('visibilitychange', onVisibilityChange);

        const onWindowFocus = () => {
          this.recoverAudio();
        };
        this.focusListener = onWindowFocus;
        window.addEventListener('focus', onWindowFocus);

        const onPageShow = () => {
          this.recoverAudio();
        };
        this.pageshowListener = onPageShow;
        window.addEventListener('pageshow', onPageShow);
      }
    }
  }

  public unlockAudio(): void {
    if (this.needsHardRecovery || !this.ctx || (this.ctx.state as any) === 'interrupted' || this.ctx.state === 'closed') {
      this.needsHardRecovery = false;
      this.resetContext();
    }

    const ctx = this.getAudioContext();
    if (ctx) {
      this.platformAdapter.prewarmAndUnlock(ctx, this.isVoiceActive);
      if (ctx.state === 'suspended' || (ctx.state as any) === 'interrupted') {
        ctx.resume().then(() => {
          if (ctx.state === 'running') {
            this.needsHardRecovery = false;
          }
          this.notifyListeners();
        }).catch(() => {
          this.attachUnlockListeners();
        });
      }
      this.getCardFanBuffer(ctx).catch(() => {});
    }
    this.notifyListeners();
  }

  public attachUnlockListeners() {
    if (typeof window === 'undefined' || this.unlockListenersAttached) return;

    const unlockEvents = ['pointerdown', 'touchstart', 'touchend', 'click', 'keydown'];
    const unlockAudio = () => {
      this.unlockAudio();
    };

    this.unlockAudioHandler = unlockAudio;
    this.unlockListenersAttached = true;
    unlockEvents.forEach((evt) => {
      window.addEventListener(evt, unlockAudio, { capture: true, passive: true });
    });
  }

  /**
   * Proactively recovers and verifies audio playback upon foreground return from multitasking/backgrounding.
   */
  public recoverAudio() {
    if (this.isMuted) return;

    // Ensure unlock listeners are armed so user interaction immediately primes the hardware
    this.attachUnlockListeners();

    // If context was closed or invalidated, perform a reset
    if (!this.ctx || this.ctx.state === 'closed' || (this.ctx.state as any) === 'interrupted') {
      this.resetContext();
      this.notifyListeners();
      return;
    }

    const ctx = this.getAudioContext();
    if (!ctx) {
      this.notifyListeners();
      return;
    }

    if (ctx.state === 'suspended' || (ctx.state as any) === 'interrupted') {
      ctx.resume().then(() => {
        if (ctx.state === 'running' && this.platformAdapter.platformName !== 'ios') {
          this.needsHardRecovery = false;
        }
        this.notifyListeners();
      }).catch((err) => {
        if (this.platformAdapter.platformName === 'ios') {
          this.needsHardRecovery = true;
        }
        this.attachUnlockListeners();
        this.notifyListeners();
        arenaLogger.warn(`[ArenaAudio] Failed to resume audio on foreground return: ${err?.message || err}`);
      });
    } else if (this.platformAdapter.platformName === 'ios') {
      // On iOS, backgrounding detaches WebKit CoreAudio output unit even if ctx.state is 'running'
      this.needsHardRecovery = true;
      this.platformAdapter.onForeground(ctx, this.isVoiceActive);
      this.notifyListeners();
    } else {
      this.needsHardRecovery = false;
      this.platformAdapter.onForeground(ctx, this.isVoiceActive);
      this.notifyListeners();
    }
  }

  /**
   * Recreates or resets the AudioContext after a fatal closure or unrecoverable error.
   */
  public recreateContext(): AudioContext | null {
    return this.resetContext();
  }

  /**
   * Cleans up the current context references and master nodes, returning a freshly bound context.
   */
  public resetContext(): AudioContext | null {
    this.needsHardRecovery = false;
    if (this.ctx) {
      try {
        if (this.ctx.state !== 'closed') {
          this.ctx.close().catch(() => {});
        }
      } catch {}
      this.ctx = null;
    }
    this.masterGainNode = null;
    this.compressorNode = null;
    this.cardFanBuffer = null;

    const freshCtx = this.getAudioContext();
    if (freshCtx) {
      this.platformAdapter.onForeground(freshCtx, this.isVoiceActive);
      this.getCardFanBuffer(freshCtx).catch(() => {});
    }
    this.notifyListeners();
    return freshCtx;
  }

  public dispose() {
    if (this.visibilityListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }
    if (this.focusListener && typeof window !== 'undefined') {
      window.removeEventListener('focus', this.focusListener);
      this.focusListener = null;
    }
    if (this.pageshowListener && typeof window !== 'undefined') {
      window.removeEventListener('pageshow', this.pageshowListener);
      this.pageshowListener = null;
    }
    if (this.unlockAudioHandler && typeof window !== 'undefined') {
      const unlockEvents = ['pointerdown', 'touchstart', 'touchend', 'click', 'keydown'];
      unlockEvents.forEach((evt) => {
        window.removeEventListener(evt, this.unlockAudioHandler!, true);
      });
      this.unlockAudioHandler = null;
      this.unlockListenersAttached = false;
    }
    this.platformAdapter.dispose();
    if (this.ctx) {
      try {
        this.ctx.close().catch(() => {});
      } catch {}
      this.ctx = null;
    }
    this.masterGainNode = null;
    this.compressorNode = null;
    this.cardFanBuffer = null;
  }

  /**
   * Initializes or returns the master audio processing bus:
   * [Sound Sources] -> [Master Gain Node] -> [Dynamics Compressor] -> [Destination]
   */
  public getMasterNode(ctx: AudioContext): AudioNode {
    if (!this.masterGainNode || (this.masterGainNode as any).context !== ctx) {
      try {
        const gainNode = ctx.createGain();
        const t = ctx.currentTime || 0;
        // Compensate for OS-level communications ducking (80% drop) when voice is active
        const initialGain = this.isVoiceActive ? 1.75 : 1.0;
        if (gainNode.gain?.setValueAtTime) {
          gainNode.gain.setValueAtTime(initialGain, t);
        } else if (gainNode.gain) {
          gainNode.gain.value = initialGain;
        }

        this.masterGainNode = gainNode;

        if (typeof ctx.createDynamicsCompressor === 'function') {
          const comp = ctx.createDynamicsCompressor();
          if (comp.threshold?.setValueAtTime) comp.threshold.setValueAtTime(-18, t);
          if (comp.knee?.setValueAtTime) comp.knee.setValueAtTime(12, t);
          if (comp.ratio?.setValueAtTime) comp.ratio.setValueAtTime(4, t);
          if (comp.attack?.setValueAtTime) comp.attack.setValueAtTime(0.003, t);
          if (comp.release?.setValueAtTime) comp.release.setValueAtTime(0.15, t);

          this.compressorNode = comp;
          gainNode.connect(comp);
          if (ctx.destination) {
            comp.connect(ctx.destination);
          }
        } else if (ctx.destination) {
          gainNode.connect(ctx.destination);
        }

        (ctx as any).__masterNode = gainNode;
      } catch {
        return (ctx as any).__masterNode || ctx.destination;
      }
    }
    return this.masterGainNode || (ctx as any).__masterNode || ctx.destination;
  }

  /**
   * Dynamically boosts game audio to counter OS communication ducking when voice chat is live.
   */
  public setVoiceActive(active: boolean) {
    this.isVoiceActive = active;
    this.platformAdapter.configureSession(active);
    if (this.ctx && this.ctx.state !== 'closed') {
      this.platformAdapter.onForeground(this.ctx, active);
    }
    if (this.masterGainNode && this.ctx && this.ctx.state !== 'closed') {
      try {
        const t = this.ctx.currentTime || 0;
        const targetGain = active ? 1.75 : 1.0;
        if (this.masterGainNode.gain?.linearRampToValueAtTime) {
          if (typeof this.masterGainNode.gain.cancelScheduledValues === 'function') {
            this.masterGainNode.gain.cancelScheduledValues(t);
          }
          if (typeof this.masterGainNode.gain.setValueAtTime === 'function') {
            this.masterGainNode.gain.setValueAtTime(this.masterGainNode.gain.value, t);
          }
          this.masterGainNode.gain.linearRampToValueAtTime(targetGain, t + 0.05);
        } else if (this.masterGainNode.gain) {
          this.masterGainNode.gain.value = targetGain;
        }
      } catch {}
    }
  }

  /**
   * Explicitly resumes or recovers the game AudioContext (e.g. after mic acquisition).
   */
  public async resumeAudio(): Promise<void> {
    const ctx = this.getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended' || (ctx.state as any) === 'interrupted') {
      try {
        await ctx.resume();
      } catch (err) {
        console.warn('[ArenaAudio] Failed to resume audio context:', err);
      }
    }
  }

  private getAudioContext(): AudioContext | null {
    if (this.isMuted) return null;

    // Detect closed context and reset
    if (this.ctx && this.ctx.state === 'closed') {
      this.ctx = null;
      this.masterGainNode = null;
      this.compressorNode = null;
      this.cardFanBuffer = null;
    }

    if (!this.ctx) {
      this.ctx = this.platformAdapter.createAudioContext();

      if (this.ctx) {
        this.getMasterNode(this.ctx);

        if (this.ctx.onstatechange !== undefined) {
          this.ctx.onstatechange = () => {
            if (this.ctx && (this.ctx.state === 'suspended' || (this.ctx.state as any) === 'interrupted')) {
              this.attachUnlockListeners();
            }
            this.notifyListeners();
          };
        }
      }
    }

    return this.ctx;
  }

  private runWithContext(fn: (ctx: AudioContext) => void) {
    if (this.isMuted) return;
    // Suppress audio synthesis when document is hidden in the background
    if (typeof document !== 'undefined' && document.hidden) return;

    let ctx = this.getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'closed') {
      ctx = this.resetContext();
      if (!ctx) return;
    }

    if (ctx.state === 'suspended' || (ctx.state as any) === 'interrupted') {
      ctx.resume().then(() => {
        if (ctx && ctx.state === 'running') {
          this.needsHardRecovery = false;
          this.notifyListeners();
        }
      }).catch(() => {
        // Autoplay policy prevented resumption outside user gesture (e.g. background bot turn).
        // Ensure unlock listeners are attached so next user tap recovers audio.
        this.attachUnlockListeners();
      });
    }

    try {
      this.getMasterNode(ctx);
      fn(ctx);
    } catch (e: any) {
      if (e?.name === 'InvalidStateError') {
        const fresh = this.resetContext();
        if (fresh) {
          try {
            this.getMasterNode(fresh);
            fn(fresh);
          } catch {}
        }
      }
    }
  }

  private async getCardFanBuffer(ctx: AudioContext): Promise<AudioBuffer | null> {
    if (this.cardFanBuffer) return this.cardFanBuffer;
    if (this.isDecodingCardFan) return null;
    this.isDecodingCardFan = true;
    try {
      const base64 = CARD_FAN_AUDIO_DATA_URI.split(',')[1];
      const binaryString = atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      this.cardFanBuffer = await ctx.decodeAudioData(bytes.buffer.slice(0));
      return this.cardFanBuffer;
    } catch (e) {
      console.warn('[ArenaAudio] Failed to decode card-fan audio', e);
      return null;
    } finally {
      this.isDecodingCardFan = false;
    }
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  public notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener();
      } catch {}
    });
  }

  public isSoundMuted(): boolean {
    return this.isMuted;
  }

  public isAudioSuspended(): boolean {
    if (this.isMuted) return true;
    if (!this.ctx) return true;
    if (this.needsHardRecovery) return true;
    return this.ctx.state === 'suspended' || (this.ctx.state as any) === 'interrupted';
  }

  public toggleMute(): boolean {
    this.setMute(!this.isMuted);
    return this.isMuted;
  }

  public setAudioRoute(route: AudioRouteType) {
    this.currentRoute = route;
  }

  public setMute(muted: boolean) {
    this.isMuted = muted;
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('atl_arena_sound_muted', muted ? 'true' : 'false');
      }
    } catch {}

    if (muted) {
      if (this.ctx && this.ctx.state === 'running') {
        try {
          this.ctx.suspend().catch(() => {});
        } catch {}
      }
    } else {
      this.unlockAudio();
    }
    this.notifyListeners();
  }

  // --- Card Audio Actions ---

  public playCardToFelt() {
    const now = Date.now();
    if (now - this.lastCardToFeltTime < 160) return;
    this.lastCardToFeltTime = now;
    console.log('[ArenaAudio] 🎵 Playing: playCardToFelt');
    this.runWithContext((ctx) => CardAudioSynthesizer.playCardToFelt(ctx));
  }

  public playCardSnap() {
    console.log('[ArenaAudio] 🎵 Playing: playCardSnap');
    this.runWithContext((ctx) => CardAudioSynthesizer.playCardSnap(ctx));
  }

  public playCardPlay() {
    this.playCardToFelt();
  }

  public playRiffleShuffle(snappy: boolean = true) {
    this.runWithContext((ctx) => CardAudioSynthesizer.playRiffleShuffle(ctx, snappy));
  }

  public playDeckCut() {
    this.runWithContext((ctx) => CardAudioSynthesizer.playDeckCut(ctx));
  }

  public playKittyTuck() {
    this.runWithContext((ctx) => CardAudioSynthesizer.playKittyTuck(ctx));
  }

  public playCardDeal() {
    this.runWithContext((ctx) => CardAudioSynthesizer.playCardDeal(ctx));
  }

  public playCardFan(duration?: number, cardCount?: number) {
    if (this.isMuted) return;
    console.log('[ArenaAudio] 🎵 Playing: playCardFan');
    this.runWithContext(async (ctx) => {
      const buffer = await this.getCardFanBuffer(ctx);
      CardAudioSynthesizer.playCardFan(ctx, buffer, duration, cardCount);
    });
  }

  public playCardFanning(duration?: number, cardCount?: number) {
    this.playCardFan(duration, cardCount);
  }

  public playCardSelect() {
    const now = Date.now();
    if (now - this.lastCardSelectTime < 60) return;
    this.lastCardSelectTime = now;
    this.runWithContext((ctx) => CardAudioSynthesizer.playCardSelect(ctx));
  }

  public playCardDiscard() {
    console.log('[ArenaAudio] 🎵 Playing: playCardDiscard');
    this.runWithContext((ctx) => CardAudioSynthesizer.playCardDiscard(ctx));
  }

  public playIllegalCard() {
    this.runWithContext((ctx) => CardAudioSynthesizer.playIllegalCard(ctx));
  }

  // --- Table Event Actions ---

  public playTrickWon() {
    const now = Date.now();
    if (now - this.lastTrickWonTime < 500) return;
    this.lastTrickWonTime = now;
    console.log('[ArenaAudio] 🎵 Playing: playTrickWon');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playTrickWon(ctx));
  }

  public playBidChime() {
    const now = Date.now();
    if (now - this.lastBidChimeTime < 180) return;
    this.lastBidChimeTime = now;
    console.log('[ArenaAudio] 🎵 Playing: playBidChime');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playBidChime(ctx));
  }

  public playPassTone() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playPassTone(ctx));
  }

  public playChatBubblePop() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playChatBubblePop(ctx));
  }

  public playSpectatorJoinSound() {
    const now = Date.now();
    if (now - this.lastSpectatorJoinTime < 350) return;
    this.lastSpectatorJoinTime = now;
    console.log('[ArenaAudio] 👁️ Playing: playSpectatorJoinSound');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playSpectatorJoinSound(ctx));
  }

  public playSpectatorLeaveSound() {
    const now = Date.now();
    if (now - this.lastSpectatorLeaveTime < 350) return;
    this.lastSpectatorLeaveTime = now;
    console.log('[ArenaAudio] 👋 Playing: playSpectatorLeaveSound');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playSpectatorLeaveSound(ctx));
  }

  public playPlayerJoinSound() {
    const now = Date.now();
    if (now - this.lastPlayerJoinTime < 350) return;
    this.lastPlayerJoinTime = now;
    console.log('[ArenaAudio] 🚪 Playing: playPlayerJoinSound');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playPlayerJoinSound(ctx));
  }

  public playTableJoinSound() {
    this.playPlayerJoinSound();
  }

  public playPlayerLeaveSound() {
    const now = Date.now();
    if (now - this.lastPlayerLeaveTime < 350) return;
    this.lastPlayerLeaveTime = now;
    console.log('[ArenaAudio] 🚪 Playing: playPlayerLeaveSound');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playPlayerLeaveSound(ctx));
  }

  public playLeaveSound() {
    this.playPlayerLeaveSound();
  }

  public playTurnTickTock() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playTurnTickTock(ctx));
  }

  public playTimerTick(isUrgent: boolean = false) {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playTimerTick(ctx, isUrgent));
  }

  public playYourBidTurn() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playYourBidTurn(ctx));
  }

  public playYourTurn() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playYourTurn(ctx));
  }

  public playScoreIncrementTick(step: number = 0) {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playScoreIncrementTick(ctx, step));
  }

  public playScoreIncrementWhoop() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playScoreIncrementWhoop(ctx));
  }

  public playScoreDecrementTick(step: number = 0) {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playScoreDecrementTick(ctx, step));
  }

  public playScoreDecrementLanding() {
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playScoreDecrementLanding(ctx));
  }

  /**
   * Bright, crystal ping chime ("Ding!") for positive series or score increments.
   */
  public playPing() {
    this.playScoreIncrementWhoop();
  }

  /**
   * Warm, resonant pong thud for defeat or score decrements.
   */
  public playPong() {
    this.playScoreDecrementLanding();
  }

  public playHearYeFanfare() {
    this.playClaimNotification();
  }

  public playClaimNotification() {
    console.log('[ArenaAudio] 🔔 Playing: playClaimNotification');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playClaimNotification(ctx));
  }

  public playHeartsBroken() {
    console.log('[ArenaAudio] 💔 Playing: playHeartsBroken');
    this.runWithContext((ctx) => GameEventAudioSynthesizer.playHeartsBroken(ctx));
  }

  // --- Hand & Match Outcome Audio ---

  /**
   * 5-tone descending sequence ("bo do do do dum") down the scale.
   * Played when getting set (failed contract) or losing a match.
   */
  public playDescending5ToneSet() {
    console.log('[ArenaAudio] 📉 Playing: playDescending5ToneSet');
    this.runWithContext((ctx) => OutcomeAudioSynthesizer.playDescending5ToneSet(ctx));
  }

  public playSetSound() {
    this.playDescending5ToneSet();
  }

  /**
   * 5-tone ascending sequence ("bo do do dum ding") up the scale ending on bell chime.
   * Played when making a bid, setting opponents, or winning a match.
   */
  public playAscending5ToneSuccess() {
    console.log('[ArenaAudio] 📈 Playing: playAscending5ToneSuccess');
    this.runWithContext((ctx) => OutcomeAudioSynthesizer.playAscending5ToneSuccess(ctx));
  }

  public playSuccessSound() {
    this.playAscending5ToneSuccess();
  }

  /**
   * Sparkling arcade/jackpot prize tone.
   * Played when a Boston is achieved.
   */
  public playBostonPrizeTone() {
    console.log('[ArenaAudio] 🏆 Playing: playBostonPrizeTone');
    this.runWithContext((ctx) => OutcomeAudioSynthesizer.playBostonPrizeTone(ctx));
  }

  /**
   * Ethereal, celestial cosmic chime sequence for Hearts "Shoot the Moon".
   */
  public playShootTheMoon() {
    console.log('[ArenaAudio] 🌙 Playing: playShootTheMoon');
    this.runWithContext((ctx) => OutcomeAudioSynthesizer.playShootTheMoon(ctx));
  }

  /**
   * Authentic classic steam locomotive whistle ("toot toot!").
   * Played when the Boston celebratory train rolls across the screen.
   */
  public playTrainWhistle() {
    console.log('[ArenaAudio] 🚂 Playing: playTrainWhistle');
    this.runWithContext((ctx) => OutcomeAudioSynthesizer.playTrainWhistle(ctx));
  }

  /**
   * Dramatic 2-tone doom ("dummmm dooooom") when match ends in defeat.
   */
  public playMatchDefeatDoom() {
    console.log('[ArenaAudio] ☠️ Playing: playMatchDefeatDoom (dummmm dooooom)');
    this.runWithContext((ctx) => OutcomeAudioSynthesizer.playMatchDefeatDoom(ctx));
  }

  public playDumDoom() {
    this.playMatchDefeatDoom();
  }
}

export const arenaAudio = new ArenaAudioEngine();
